import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2, Star, Clock, Users, Upload, Download } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SurveyTemplateDialog } from "./SurveyTemplateDialog";
import { CSVImportDialog } from "./csv/CSVImportDialog";
import { CSVExportDialog } from "./csv/CSVExportDialog";

interface SurveyTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  tool_id: string;
  is_featured: boolean;
  estimated_duration_minutes: number | null;
  target_audience: string | null;
  created_at: string;
  updated_at: string;
  tools: {
    name: string;
  } | null;
  _count?: {
    survey_template_questions: number;
  };
}

export const SurveyTemplatesManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SurveyTemplate | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const { data: templates, isLoading, refetch } = useQuery({
    queryKey: ['admin-survey-templates'],
    queryFn: async () => {
      // First get the templates
      const { data: templatesData, error: templatesError } = await supabase
        .from('survey_templates')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('name');

      if (templatesError) throw templatesError;

      // Then get tools for each template
      const toolIds = templatesData.map(t => t.tool_id).filter(Boolean);
      const { data: toolsData } = await supabase
        .from('tools')
        .select('id, name')
        .in('id', toolIds);

      // Get question counts for each template
      const templateIds = templatesData.map(t => t.id);
      const { data: questionsData } = await supabase
        .from('survey_template_questions')
        .select('template_id')
        .in('template_id', templateIds);

      // Create lookup maps
      const toolsMap = new Map(toolsData?.map(t => [t.id, t]) || []);
      const questionCounts = questionsData?.reduce((acc, q) => {
        acc[q.template_id] = (acc[q.template_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Transform data
      return templatesData.map(template => ({
        ...template,
        tools: toolsMap.get(template.tool_id) || null,
        _count: {
          survey_template_questions: questionCounts[template.id] || 0
        }
      }));
    },
  });

  const filteredTemplates = templates?.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.tools?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (template: SurveyTemplate) => {
    setSelectedTemplate(template);
    // Use setTimeout to ensure state is set before opening dialog
    setTimeout(() => {
      setIsDialogOpen(true);
    }, 0);
  };

  const handleAdd = () => {
    setSelectedTemplate(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this survey template? This will also remove all associated question relationships.")) return;

    try {
      // First delete survey_template_questions relationships
      await supabase
        .from('survey_template_questions')
        .delete()
        .eq('template_id', templateId);

      // Then delete the template
      const { error } = await supabase
        .from('survey_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting survey template:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search survey templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setIsImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={() => setIsExportOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Survey Template
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Templates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{templates?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Featured Templates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {templates?.filter(t => t.is_featured).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(templates?.map(t => t.category)).size || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates table */}
      <Card>
        <CardHeader>
          <CardTitle>Survey Templates ({filteredTemplates?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Tool</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Audience</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates?.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{template.name}</span>
                        {template.is_featured && (
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground truncate max-w-md">
                          {template.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">
                      {template.category}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {template.tools?.name || 'No Tool'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-center">
                      {template._count?.survey_template_questions || 0}
                    </div>
                  </TableCell>
                  <TableCell>
                    {template.estimated_duration_minutes && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{template.estimated_duration_minutes}min</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {template.target_audience && (
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{template.target_audience}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={template.is_featured ? "default" : "secondary"}>
                      {template.is_featured ? "Featured" : "Standard"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SurveyTemplateDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        template={selectedTemplate}
        onSuccess={() => {
          refetch();
          setIsDialogOpen(false);
        }}
      />

      <CSVImportDialog
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        entity="survey-templates"
        onSuccess={() => refetch()}
      />
      <CSVExportDialog
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        entity="survey-templates"
      />
    </div>
  );
};